/**
 * An in-page link that glides to its section (owner, 2026-11-01: the
 * instant jump felt broken). Used by the residence top's listing counts
 * ("45 elan" goes to the sale section). A keyboard press and
 * prefers-reduced-motion jump instead; modified clicks keep the browser's
 * own behaviour.
 *
 * Blocks above a section load when they come near the screen (the
 * good-value listings around a residence, the comments) and grow after
 * the jump, pushing the section down; iOS Safari has no scroll anchoring
 * to hold it. So after a jump the section is kept at the top while the
 * page settles, for two and a half seconds or until the reader scrolls.
 */
import type React from "react";

const FOLLOW_MS = 2500;
// The glide's longest run; browsers without `scrollend` stop re-aiming
// smoothly after it.
const GLIDE_MS = 900;

export function glideToSection(e: React.MouseEvent<HTMLAnchorElement>, id: string): void {
  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
  const target = document.getElementById(id);
  if (!target) return;
  e.preventDefault();
  // detail 0 is a keyboard press: no animation on keyboard actions.
  let gliding = e.detail > 0 && !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (window.location.hash !== `#${id}`) window.history.pushState(null, "", `#${id}`);
  // scroll-margin keeps the heading clear of the sticky header.
  const align = () => target.scrollIntoView({ behavior: gliding ? "smooth" : "auto", block: "start" });
  align();
  const main = document.querySelector("main");
  if (!main || typeof ResizeObserver === "undefined") return;
  const settle = () => { gliding = false; };
  window.addEventListener("scrollend", settle, { once: true });
  window.setTimeout(settle, GLIDE_MS);
  // A block above the section that grows re-aims the scroll: the glide
  // while it runs, a straight correction after it.
  let done = false;
  const ro = new ResizeObserver(() => { if (!done) align(); });
  const stop = () => {
    if (done) return;
    done = true;
    ro.disconnect();
    window.removeEventListener("wheel", stop);
    window.removeEventListener("touchstart", stop);
    window.removeEventListener("keydown", stop);
  };
  window.addEventListener("wheel", stop, { passive: true });
  window.addEventListener("touchstart", stop, { passive: true });
  window.addEventListener("keydown", stop);
  ro.observe(main);
  window.setTimeout(stop, FOLLOW_MS);
}
